/**
 * Guest Storage Service
 * Manages localStorage for guest users (unauthenticated users)
 * Stores: notes, highlights, activity completions, stats, leaderboard, shop inventory
 */

const STORAGE_PREFIX = 'budlee_ai_guest_';
const LEGACY_STORAGE_PREFIX = 'homie_ai_guest_';
const NOTES_KEY = `${STORAGE_PREFIX}notes`;
const HIGHLIGHTS_KEY = `${STORAGE_PREFIX}highlights`;
const BOOKMARKS_KEY = `${STORAGE_PREFIX}bookmarks`;
const ACTIVITY_COMPLETIONS_KEY = `${STORAGE_PREFIX}activity_completions`;
const STATS_KEY = `${STORAGE_PREFIX}stats`;
const LEADERBOARD_KEY = `${STORAGE_PREFIX}leaderboard`;
const SHOP_INVENTORY_KEY = `${STORAGE_PREFIX}shop_inventory`;
const ADVANCED_INTERACTIONS_KEY = `${STORAGE_PREFIX}advanced_interactions`;
const STUDENT_NOTES_KEY = `${STORAGE_PREFIX}student_notes`;
const CURIOSITY_INTERACTIONS_KEY = `${STORAGE_PREFIX}curiosity_interactions`;

// Migrate any existing legacy keys to budlee_ai_guest_ prefix to ensure zero data loss
export const migrateLegacyGuestStorage = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    const knownKeys = [
      'notes', 'highlights', 'bookmarks', 'activity_completions', 'stats',
      'leaderboard', 'shop_inventory', 'advanced_interactions', 'student_notes',
      'curiosity_interactions', 'chat_sessions', 'quests'
    ];
    knownKeys.forEach(key => {
      const legacyKey = `${LEGACY_STORAGE_PREFIX}${key}`;
      const newKey = `${STORAGE_PREFIX}${key}`;
      const legacyVal = localStorage.getItem(legacyKey);
      if (legacyVal && !localStorage.getItem(newKey)) {
        localStorage.setItem(newKey, legacyVal);
        localStorage.removeItem(legacyKey);
      }
    });
  } catch (e) {
    // Graceful fallback if localStorage is disabled or restricted
  }
};

// Execute immediately when bundle/module loads
migrateLegacyGuestStorage();

/**
 * Initialize localStorage with empty data structures if needed
 */
export const initializeGuestStorage = () => {
  migrateLegacyGuestStorage();

  if (!localStorage.getItem(NOTES_KEY)) {
    localStorage.setItem(NOTES_KEY, JSON.stringify({}));
  }
  if (!localStorage.getItem(HIGHLIGHTS_KEY)) {
    localStorage.setItem(HIGHLIGHTS_KEY, JSON.stringify({}));
  }
  if (!localStorage.getItem(BOOKMARKS_KEY)) {
    localStorage.setItem(BOOKMARKS_KEY, JSON.stringify({}));
  }
  if (!localStorage.getItem(ACTIVITY_COMPLETIONS_KEY)) {
    localStorage.setItem(ACTIVITY_COMPLETIONS_KEY, JSON.stringify({}));
  }
  if (!localStorage.getItem(STATS_KEY)) {
    localStorage.setItem(STATS_KEY, JSON.stringify({
      total_xp: 0,
      gems: 0,
      hearts: 5,
      current_streak: 0,
      last_activity_date: null
    }));
  }
  if (!localStorage.getItem(LEADERBOARD_KEY)) {
    localStorage.setItem(LEADERBOARD_KEY, JSON.stringify({
      rank: 0,
      total_players: 0,
      entries: []
    }));
  }
  if (!localStorage.getItem(SHOP_INVENTORY_KEY)) {
    localStorage.setItem(SHOP_INVENTORY_KEY, JSON.stringify([]));
  }
  if (!localStorage.getItem(ADVANCED_INTERACTIONS_KEY)) {
    localStorage.setItem(ADVANCED_INTERACTIONS_KEY, JSON.stringify({
      completed_videos: [],
      completed_articles: [],
      course_progress: {}
    }));
  }
  if (!localStorage.getItem(STUDENT_NOTES_KEY)) {
    localStorage.setItem(STUDENT_NOTES_KEY, JSON.stringify({}));
  }
  if (!localStorage.getItem(CURIOSITY_INTERACTIONS_KEY)) {
    localStorage.setItem(CURIOSITY_INTERACTIONS_KEY, JSON.stringify({
      completed_videos: [],
      completed_articles: [],
      completed_podcasts: []
    }));
  }
};

// ============= NOTES =============

/**
 * Get all notes from localStorage
 * @returns {Object} Notes grouped by chapter_id: { chapter_id: [notes] }
 */
export const getGuestNotes = () => {
  try {
    const data = localStorage.getItem(NOTES_KEY);
    return data ? JSON.parse(data) : {};
  } catch (error) {
    console.error('Error reading notes from localStorage:', error);
    return {};
  }
};

/**
 * Get notes for a specific chapter
 * @param {number} chapterId - Chapter ID
 * @returns {Array} Array of notes for the chapter
 */
export const getGuestNotesByChapter = (chapterId) => {
  const allNotes = getGuestNotes();
  return allNotes[chapterId] || [];
};

/**
 * Add a note for guest user
 * @param {Object} note - Note object { heading, content, color, page_number, chapter }
 * @returns {Object} Created note with ID
 */
export const addGuestNote = (note) => {
  const allNotes = getGuestNotes();
  const chapterId = note.chapter;
  
  if (!allNotes[chapterId]) {
    allNotes[chapterId] = [];
  }
  
  const newNote = {
    id: `guest_note_${Date.now()}_${Math.random()}`,
    ...note,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    is_guest: true
  };
  
  allNotes[chapterId].push(newNote);
  localStorage.setItem(NOTES_KEY, JSON.stringify(allNotes));
  
  return newNote;
};

/**
 * Update a guest note
 * @param {string} noteId - Note ID
 * @param {number} chapterId - Chapter ID
 * @param {Object} updates - Fields to update
 */
export const updateGuestNote = (noteId, chapterId, updates) => {
  const allNotes = getGuestNotes();
  
  if (!allNotes[chapterId]) return null;
  
  const noteIndex = allNotes[chapterId].findIndex(n => n.id === noteId);
  if (noteIndex === -1) return null;
  
  allNotes[chapterId][noteIndex] = {
    ...allNotes[chapterId][noteIndex],
    ...updates,
    updated_at: new Date().toISOString()
  };
  
  localStorage.setItem(NOTES_KEY, JSON.stringify(allNotes));
  return allNotes[chapterId][noteIndex];
};

/**
 * Delete a guest note
 * @param {string} noteId - Note ID
 * @param {number} chapterId - Chapter ID
 */
export const deleteGuestNote = (noteId, chapterId) => {
  const allNotes = getGuestNotes();
  
  if (!allNotes[chapterId]) return false;
  
  allNotes[chapterId] = allNotes[chapterId].filter(n => n.id !== noteId);
  localStorage.setItem(NOTES_KEY, JSON.stringify(allNotes));
  
  return true;
};

// ============= HIGHLIGHTS =============

/**
 * Get all highlights from localStorage
 * @returns {Object} Highlights grouped by chapter_id: { chapter_id: [highlights] }
 */
export const getGuestHighlights = () => {
  try {
    const data = localStorage.getItem(HIGHLIGHTS_KEY);
    return data ? JSON.parse(data) : {};
  } catch (error) {
    console.error('Error reading highlights from localStorage:', error);
    return {};
  }
};

/**
 * Get highlights for a specific chapter
 * @param {number} chapterId - Chapter ID
 * @returns {Array} Array of highlights for the chapter
 */
export const getGuestHighlightsByChapter = (chapterId) => {
  const allHighlights = getGuestHighlights();
  return allHighlights[chapterId] || [];
};

/**
 * Add a highlight for guest user
 * @param {Object} highlight - Highlight object { text, color, page_number, chapter, section_name }
 * @returns {Object} Created highlight with ID
 */
export const addGuestHighlight = (highlight) => {
  const allHighlights = getGuestHighlights();
  const chapterId = highlight.chapter;
  
  if (!allHighlights[chapterId]) {
    allHighlights[chapterId] = [];
  }
  
  const newHighlight = {
    id: `guest_highlight_${Date.now()}_${Math.random()}`,
    ...highlight,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    is_guest: true
  };
  
  allHighlights[chapterId].push(newHighlight);
  localStorage.setItem(HIGHLIGHTS_KEY, JSON.stringify(allHighlights));
  
  return newHighlight;
};

/**
 * Update a guest highlight
 * @param {string} highlightId - Highlight ID
 * @param {number} chapterId - Chapter ID
 * @param {Object} updates - Fields to update
 */
export const updateGuestHighlight = (highlightId, chapterId, updates) => {
  const allHighlights = getGuestHighlights();
  
  if (!allHighlights[chapterId]) return null;
  
  const highlightIndex = allHighlights[chapterId].findIndex(h => h.id === highlightId);
  if (highlightIndex === -1) return null;
  
  allHighlights[chapterId][highlightIndex] = {
    ...allHighlights[chapterId][highlightIndex],
    ...updates,
    updated_at: new Date().toISOString()
  };
  
  localStorage.setItem(HIGHLIGHTS_KEY, JSON.stringify(allHighlights));
  return allHighlights[chapterId][highlightIndex];
};

/**
 * Delete a guest highlight
 * @param {string} highlightId - Highlight ID
 * @param {number} chapterId - Chapter ID
 */
export const deleteGuestHighlight = (highlightId, chapterId) => {
  const allHighlights = getGuestHighlights();
  
  if (!allHighlights[chapterId]) return false;
  
  allHighlights[chapterId] = allHighlights[chapterId].filter(h => h.id !== highlightId);
  localStorage.setItem(HIGHLIGHTS_KEY, JSON.stringify(allHighlights));
  
  return true;
};

// ============= BOOKMARKS =============

/**
 * Get all bookmarks from localStorage
 * @returns {Object} Bookmarks grouped by chapter_id: { chapter_id: [bookmarks] }
 */
export const getGuestBookmarks = () => {
  try {
    const data = localStorage.getItem(BOOKMARKS_KEY);
    return data ? JSON.parse(data) : {};
  } catch (error) {
    console.error('Error reading bookmarks from localStorage:', error);
    return {};
  }
};

/**
 * Get bookmarks for a specific chapter
 * @param {number} chapterId - Chapter ID
 * @returns {Array} Array of bookmarks for the chapter
 */
export const getGuestBookmarksByChapter = (chapterId) => {
  const allBookmarks = getGuestBookmarks();
  return allBookmarks[chapterId] || [];
};

/**
 * Add a bookmark for guest user
 * @param {Object} bookmark - Bookmark object
 * @returns {Object} Created bookmark with ID
 */
export const addGuestBookmark = (bookmark) => {
  const allBookmarks = getGuestBookmarks();
  const chapterId = bookmark.chapter_id;

  if (!allBookmarks[chapterId]) {
    allBookmarks[chapterId] = [];
  }

  const existingIndex = allBookmarks[chapterId].findIndex(
    b => String(b.content_id || '') === String(bookmark.content_id || '')
      && String(b.bookmark_type || '') === String(bookmark.bookmark_type || '')
  );

  const newBookmark = {
    id: `guest_bookmark_${Date.now()}_${Math.random()}`,
    ...bookmark,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    is_guest: true
  };

  if (existingIndex !== -1) {
    allBookmarks[chapterId][existingIndex] = {
      ...allBookmarks[chapterId][existingIndex],
      ...newBookmark
    };
  } else {
    allBookmarks[chapterId].push(newBookmark);
  }

  localStorage.setItem(BOOKMARKS_KEY, JSON.stringify(allBookmarks));
  return existingIndex !== -1 ? allBookmarks[chapterId][existingIndex] : newBookmark;
};

/**
 * Delete a guest bookmark
 * @param {string} bookmarkId - Bookmark ID
 * @param {number} [chapterId] - Chapter ID (optional)
 */
export const deleteGuestBookmark = (bookmarkId, chapterId = null) => {
  const allBookmarks = getGuestBookmarks();

  if (chapterId && allBookmarks[chapterId]) {
    const originalLength = allBookmarks[chapterId].length;
    allBookmarks[chapterId] = allBookmarks[chapterId].filter(b => b.id !== bookmarkId);
    if (allBookmarks[chapterId].length === originalLength) return false;
    localStorage.setItem(BOOKMARKS_KEY, JSON.stringify(allBookmarks));
    return true;
  }

  let removed = false;
  Object.keys(allBookmarks).forEach((key) => {
    const originalLength = allBookmarks[key].length;
    allBookmarks[key] = allBookmarks[key].filter(b => b.id !== bookmarkId);
    if (allBookmarks[key].length !== originalLength) {
      removed = true;
    }
  });

  if (removed) {
    localStorage.setItem(BOOKMARKS_KEY, JSON.stringify(allBookmarks));
  }

  return removed;
};

// ============= ACTIVITY COMPLETIONS =============

/**
 * Get all activity completions from localStorage
 * @returns {Object} Completions grouped by chapter_id: { chapter_id: [completions] }
 */
export const getGuestActivityCompletions = () => {
  try {
    const data = localStorage.getItem(ACTIVITY_COMPLETIONS_KEY);
    return data ? JSON.parse(data) : {};
  } catch (error) {
    console.error('Error reading activity completions from localStorage:', error);
    return {};
  }
};

/**
 * Get activity completions for a specific chapter
 * @param {number} chapterId - Chapter ID
 * @returns {Array} Array of completions for the chapter
 */
export const getGuestActivityCompletionsByChapter = (chapterId) => {
  const allCompletions = getGuestActivityCompletions();
  return allCompletions[chapterId] || [];
};

/**
 * Check if a specific annotation is completed
 * @param {number} chapterId - Chapter ID
 * @param {number} annotationId - Annotation ID
 * @returns {Object|null} Completion record or null
 */
export const getGuestActivityCompletion = (chapterId, annotationId) => {
  const completions = getGuestActivityCompletionsByChapter(chapterId);
  return completions.find(c => c.annotation === annotationId) || null;
};

/**
 * Mark activity as completed for guest user
 * @param {Object} completion - Completion object { annotation, activity_type, xp_earned, chapter }
 * @returns {Object} Created completion with ID
 */
export const addGuestActivityCompletion = (completion) => {
  const allCompletions = getGuestActivityCompletions();
  const chapterId = completion.chapter;
  
  if (!allCompletions[chapterId]) {
    allCompletions[chapterId] = [];
  }
  
  // Check if this annotation is already completed
  const existingIndex = allCompletions[chapterId].findIndex(
    c => c.annotation === completion.annotation
  );
  
  const newCompletion = {
    id: `guest_completion_${Date.now()}_${Math.random()}`,
    ...completion,
    completed_at: new Date().toISOString(),
    is_guest: true,
    is_recompletion: existingIndex !== -1
  };
  
  if (existingIndex !== -1) {
    // Update existing completion
    allCompletions[chapterId][existingIndex] = newCompletion;
  } else {
    // Add new completion
    allCompletions[chapterId].push(newCompletion);
  }
  
  localStorage.setItem(ACTIVITY_COMPLETIONS_KEY, JSON.stringify(allCompletions));
  
  return newCompletion;
};

/**
 * Get total XP earned in a chapter for guest user
 * @param {number} chapterId - Chapter ID
 * @returns {number} Total XP earned
 */
export const getGuestChapterXP = (chapterId) => {
  const completions = getGuestActivityCompletionsByChapter(chapterId);
  return completions.reduce((total, c) => total + (c.xp_earned || 0), 0);
};

/**
 * Reset all guest data (for logout or reset scenario)
 */
export const clearGuestStorage = () => {
  localStorage.removeItem(NOTES_KEY);
  localStorage.removeItem(HIGHLIGHTS_KEY);
  localStorage.removeItem(BOOKMARKS_KEY);
  localStorage.removeItem(ACTIVITY_COMPLETIONS_KEY);
  localStorage.removeItem(STUDENT_NOTES_KEY);
  localStorage.removeItem(CURIOSITY_INTERACTIONS_KEY);
};

/**
 * Get all guest data (for debugging or export)
 */
export const getAllGuestData = () => {
  return {
    notes: getGuestNotes(),
    highlights: getGuestHighlights(),
    bookmarks: getGuestBookmarks(),
    activityCompletions: getGuestActivityCompletions(),
    stats: getGuestStats(),
    leaderboard: getGuestLeaderboard(),
    shopInventory: getGuestShopInventory()
  };
};

// ============= STATS (XP, Gems, Hearts) =============

/**
 * Get guest user stats
 * @returns {Object} Stats object { total_xp, gems, hearts, current_streak, last_activity_date }
 */
export const getGuestStats = () => {
  try {
    const data = localStorage.getItem(STATS_KEY);
    return data ? JSON.parse(data) : {
      total_xp: 0,
      weekly_xp: 0,
      monthly_xp: 0,
      gems: 0,
      hearts: 5,
      current_streak: 0,
      last_activity_date: null,
      weekly_xp_reset_date: null,
      monthly_xp_reset_date: null
    };
  } catch (error) {
    console.error('Error reading stats from localStorage:', error);
    return {
      total_xp: 0,
      weekly_xp: 0,
      monthly_xp: 0,
      gems: 0,
      hearts: 5,
      current_streak: 0,
      last_activity_date: null,
      weekly_xp_reset_date: null,
      monthly_xp_reset_date: null
    };
  }
};

/**
 * Update guest user stats
 * @param {Object} updates - Fields to update { total_xp, gems, hearts, current_streak, last_activity_date }
 */
export const updateGuestStats = (updates) => {
  const stats = getGuestStats();
  const newStats = {
    ...stats,
    ...updates,
    updated_at: new Date().toISOString()
  };
  localStorage.setItem(STATS_KEY, JSON.stringify(newStats));
  return newStats;
};

/**
 * Add XP to guest user's total
 * @param {number} xpAmount - Amount of XP to add
 */
export const addGuestXP = (xpAmount) => {
  const stats = getGuestStats();
  const now = new Date();
  const currentWeek = Math.ceil((now.getDate()) / 7);
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  
  // Check if we need to reset weekly XP (new week)
  let weeklyXP = stats.weekly_xp || 0;
  let weeklyResetDate = stats.weekly_xp_reset_date;
  if (weeklyResetDate) {
    const resetDate = new Date(weeklyResetDate);
    const resetWeek = Math.ceil((resetDate.getDate()) / 7);
    const resetMonth = resetDate.getMonth();
    const resetYear = resetDate.getFullYear();
    if (currentWeek !== resetWeek || currentMonth !== resetMonth || currentYear !== resetYear) {
      weeklyXP = 0; // Reset to new week
      weeklyResetDate = now.toISOString();
    }
  } else {
    weeklyResetDate = now.toISOString();
  }
  
  // Check if we need to reset monthly XP (new month)
  let monthlyXP = stats.monthly_xp || 0;
  let monthlyResetDate = stats.monthly_xp_reset_date;
  if (monthlyResetDate) {
    const resetDate = new Date(monthlyResetDate);
    const resetMonth = resetDate.getMonth();
    const resetYear = resetDate.getFullYear();
    if (currentMonth !== resetMonth || currentYear !== resetYear) {
      monthlyXP = 0; // Reset to new month
      monthlyResetDate = now.toISOString();
    }
  } else {
    monthlyResetDate = now.toISOString();
  }
  
  return updateGuestStats({
    total_xp: (stats.total_xp || 0) + xpAmount,
    weekly_xp: weeklyXP + xpAmount,
    monthly_xp: monthlyXP + xpAmount,
    weekly_xp_reset_date: weeklyResetDate,
    monthly_xp_reset_date: monthlyResetDate
  });
};

/**
 * Add gems to guest user
 * @param {number} gemAmount - Amount of gems to add
 */
export const addGuestGems = (gemAmount) => {
  const stats = getGuestStats();
  return updateGuestStats({
    gems: (stats.gems || 0) + gemAmount
  });
};

/**
 * Deduct gems from guest user (for purchases)
 * @param {number} gemAmount - Amount of gems to deduct
 * @returns {Object|null} Updated stats or null if insufficient gems
 */
export const deductGuestGems = (gemAmount) => {
  const stats = getGuestStats();
  if ((stats.gems || 0) < gemAmount) {
    return null; // Insufficient gems
  }
  return updateGuestStats({
    gems: stats.gems - gemAmount
  });
};

/**
 * Update hearts for guest user
 * @param {number} newHearts - New heart count
 */
export const updateGuestHearts = (newHearts) => {
  return updateGuestStats({
    hearts: newHearts
  });
};

/**
 * Update streak for guest user
 * @param {number} newStreak - New streak count
 */
export const updateGuestStreak = (newStreak) => {
  return updateGuestStats({
    current_streak: newStreak,
    last_activity_date: new Date().toISOString()
  });
};

// ============= LEADERBOARD (Guest-only local leaderboard) =============

/**
 * Get guest leaderboard data
 * @returns {Object} Leaderboard object { rank, total_players, entries }
 */
export const getGuestLeaderboard = () => {
  try {
    const data = localStorage.getItem(LEADERBOARD_KEY);
    if (!data) {
      return { rank: 0, total_players: 0, entries: [] };
    }
    return JSON.parse(data);
  } catch (error) {
    console.error('Error reading leaderboard from localStorage:', error);
    return { rank: 0, total_players: 0, entries: [] };
  }
};

/**
 * Update guest leaderboard (shows guest's own entry)
 * @param {Object} guestEntry - Guest's leaderboard entry { name, xp, rank }
 */
export const updateGuestLeaderboard = (guestEntry) => {
  const stats = getGuestStats();
  const leaderboard = {
    rank: guestEntry.rank || 1,
    total_players: 1,
    entries: [{
      id: 'guest_self',
      name: guestEntry.name || 'You (Guest)',
      full_name: guestEntry.name || 'You (Guest)',
      username: 'guest_user',
      total_xp: stats.total_xp || 0,
      period_xp: stats.monthly_xp || 0,
      weekly_xp: stats.weekly_xp || 0,
      monthly_xp: stats.monthly_xp || 0,
      rank: guestEntry.rank || 1,
      badge: guestEntry.rank === 1 ? '👑' : '⭐',
      is_guest: true
    }]
  };
  localStorage.setItem(LEADERBOARD_KEY, JSON.stringify(leaderboard));
  return leaderboard;
};

// ============= SHOP INVENTORY =============

/**
 * Get guest shop inventory
 * @returns {Array} Array of owned shop items
 */
export const getGuestShopInventory = () => {
  try {
    const data = localStorage.getItem(SHOP_INVENTORY_KEY);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error('Error reading shop inventory from localStorage:', error);
    return [];
  }
};

/**
 * Add item to guest shop inventory
 * @param {Object} item - Shop item to add
 */
export const addGuestShopItem = (item) => {
  const inventory = getGuestShopInventory();
  
  // Check if item already exists (for non-consumable items)
  if (item.item_type !== 'BOOSTER' && item.item_type !== 'PROTECTOR') {
    const exists = inventory.some(i => i.shop_item_id === item.id || i.shop_item?.id === item.id);
    if (exists) return inventory; // Item already owned
  }
  
  const inventoryItem = {
    id: `guest_inv_${Date.now()}_${Math.random()}`,
    shop_item: item,
    shop_item_id: item.id,
    acquired_at: new Date().toISOString(),
    is_equipped: false,
    is_guest: true
  };
  
  inventory.push(inventoryItem);
  localStorage.setItem(SHOP_INVENTORY_KEY, JSON.stringify(inventory));
  return inventory;
};

/**
 * Check if guest owns a shop item
 * @param {number} itemId - Shop item ID
 * @returns {boolean} True if owned
 */
export const hasGuestShopItem = (itemId) => {
  const inventory = getGuestShopInventory();
  return inventory.some(i => i.shop_item_id === itemId || i.shop_item?.id === itemId);
};

/**
 * Equip/unequip item for guest user
 * @param {number} itemId - Shop item ID
 * @param {boolean} equipped - Whether to equip or unequip
 */
export const setGuestItemEquipped = (itemId, equipped = true) => {
  const inventory = getGuestShopInventory();
  const item = inventory.find(i => i.shop_item_id === itemId || i.shop_item?.id === itemId);
  
  if (item) {
    item.is_equipped = equipped;
    localStorage.setItem(SHOP_INVENTORY_KEY, JSON.stringify(inventory));
  }
  
  return inventory;
};
// ============= ADVANCED TOPICS INTERACTIONS =============

/**
 * Get guest advanced topics interactions
 * @returns {Object} Interactions object { completed_videos: [], completed_articles: [], course_progress: {} }
 */
export const getGuestAdvancedInteractions = () => {
    try {
        const data = localStorage.getItem(ADVANCED_INTERACTIONS_KEY);
        return data ? JSON.parse(data) : { completed_videos: [], completed_articles: [], course_progress: {} };
    } catch (error) {
        console.error('Error reading advanced interactions from localStorage:', error);
        return { completed_videos: [], completed_articles: [], course_progress: {} };
    }
};

/**
 * Record an advanced topic interaction for guest user
 * @param {string} contentType - 'video', 'article', or 'course_item'
 * @param {number|string} contentId - ID of the content
 * @param {string} interactionType - 'viewed' or 'completed'
 * @param {Object} [meta] - Additional metadata (e.g. courseId, points)
 */
export const recordGuestAdvancedInteraction = (contentType, contentId, interactionType, meta = {}) => {
    const interactions = getGuestAdvancedInteractions();
    let updated = false;

    if (interactionType === 'completed' || interactionType === 'viewed') {
        if (contentType === 'video') {
            if (!interactions.completed_videos.includes(contentId)) {
                interactions.completed_videos.push(contentId);
                updated = true;
                if (interactionType === 'completed' && meta.points) addGuestXP(meta.points);
            }
        } else if (contentType === 'article') {
            if (!interactions.completed_articles.includes(contentId)) {
                interactions.completed_articles.push(contentId);
                updated = true;
                if (interactionType === 'completed' && meta.points) addGuestXP(meta.points);
            }
        } else if (contentType === 'course_item') {
            const courseId = meta.courseId;
            if (courseId) {
                if (!interactions.course_progress) interactions.course_progress = {};
                const progressKey = `course_${courseId}_items`;
                if (!interactions[progressKey]) interactions[progressKey] = [];
                if (!interactions[progressKey].includes(contentId)) {
                    interactions[progressKey].push(contentId);
                    updated = true;
                    if (interactionType === 'completed' && meta.points) addGuestXP(meta.points);
                }
            }
        } else if (contentType === 'course') {
            // Track overall course progress percentage if provided
            if (meta.progress_percentage !== undefined) {
                if (!interactions.course_progress) interactions.course_progress = {};
                interactions.course_progress[contentId] = meta.progress_percentage;
                updated = true;
                if (meta.progress_percentage === 100 && meta.points) addGuestXP(meta.points);
            }
        }
    }

    if (updated) {
        localStorage.setItem(ADVANCED_INTERACTIONS_KEY, JSON.stringify(interactions));
    }
    return interactions;
};

// ============= STUDENT WRITTEN NOTES =============

/**
 * Get guest student notes
 * @returns {Object} Notes grouped by chapter_id
 */
export const getGuestStudentNotes = () => {
    try {
        const data = localStorage.getItem(STUDENT_NOTES_KEY);
        return data ? JSON.parse(data) : {};
    } catch (error) {
        console.error('Error reading student notes from localStorage:', error);
        return {};
    }
};

/**
 * Save/Duplicate a student note for guest
 * @param {Object} note - Note object
 * @param {string|number} chapterId - Chapter ID
 */
export const addGuestStudentNote = (note, chapterId) => {
    const allNotes = getGuestStudentNotes();
    const cid = String(chapterId);
    
    if (!allNotes[cid]) {
        allNotes[cid] = [];
    }

    const newNote = {
        ...note,
        id: `guest_student_note_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        chapter_id: chapterId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        is_guest: true,
        user: { id: 'guest', full_name: 'Guest User' }
    };

    allNotes[cid].push(newNote);
    localStorage.setItem(STUDENT_NOTES_KEY, JSON.stringify(allNotes));
    return newNote;
};

/**
 * Delete a guest student note
 */
export const deleteGuestStudentNote = (noteId, chapterId) => {
    const allNotes = getGuestStudentNotes();
    const cid = String(chapterId);
    
    if (!allNotes[cid]) return false;
    
    allNotes[cid] = allNotes[cid].filter(n => n.id !== noteId);
    localStorage.setItem(STUDENT_NOTES_KEY, JSON.stringify(allNotes));
    return true;
};

// ============= CHAT SESSIONS =============

const CHAT_SESSIONS_KEY = `${STORAGE_PREFIX}chat_sessions`;

/**
 * Get all guest chat sessions
 */
export const getGuestChatSessions = () => {
    try {
        const data = localStorage.getItem(CHAT_SESSIONS_KEY);
        return data ? JSON.parse(data) : [];
    } catch (error) {
        console.error('Error reading guest chat sessions:', error);
        return [];
    }
};

/**
 * Track or update a chat session for guest user history
 * @param {Object} session - Session object from API
 */
export const trackGuestChatSession = (session) => {
    if (!session || !session.session_id) return;
    
    const sessions = getGuestChatSessions();
    const existingIndex = sessions.findIndex(s => s.session_id === session.session_id);
    
    const sessionData = {
        session_id: session.session_id,
        title: session.title || 'Untitled Chat',
        subject_id: session.subject_id,
        subject_name: session.subject_name,
        chapter_id: session.chapter_id,
        chapter_name: session.chapter_name,
        page_context: session.page_context || 'chat',
        curiosity_content_id: session.curiosity_content_id || null,
        topic_id: session.topic_id || null,
        last_message_at: session.last_message_at || new Date().toISOString(),
        message_count: session.message_count || 0,
        is_active: session.is_active,
        created_at: session.created_at || new Date().toISOString(),
        updated_at: new Date().toISOString()
    };

    if (existingIndex !== -1) {
        // Update existing entry
        sessions[existingIndex] = { ...sessions[existingIndex], ...sessionData };
    } else {
        // Add new entry to the top
        sessions.unshift(sessionData);
    }
    
    // Keep only last 50 sessions to avoid bloating localStorage
    const trimmedSessions = sessions.slice(0, 50);
    localStorage.setItem(CHAT_SESSIONS_KEY, JSON.stringify(trimmedSessions));
};

/**
 * Update message count/timestamp for a specific session
 */
export const updateGuestChatSessionStats = (sessionId, stats) => {
    const sessions = getGuestChatSessions();
    const index = sessions.findIndex(s => s.session_id === sessionId);
    
    if (index !== -1) {
        sessions[index] = {
            ...sessions[index],
            ...stats,
            updated_at: new Date().toISOString()
        };
        localStorage.setItem(CHAT_SESSIONS_KEY, JSON.stringify(sessions));
    }
};

/**
 * Delete a guest chat session from history
 */
export const deleteGuestChatSession = (sessionId) => {
    const sessions = getGuestChatSessions();
    const filtered = sessions.filter(s => s.session_id !== sessionId);
    localStorage.setItem(CHAT_SESSIONS_KEY, JSON.stringify(filtered));
};

// ============= CURIOSITY INTERACTIONS =============

/**
 * Get guest curiosity center interactions
 */
export const getGuestCuriosityInteractions = () => {
    try {
        const data = localStorage.getItem(CURIOSITY_INTERACTIONS_KEY);
        return data ? JSON.parse(data) : { completed_videos: [], completed_articles: [], completed_podcasts: [] };
    } catch (error) {
        console.error('Error reading curiosity interactions from localStorage:', error);
        return { completed_videos: [], completed_articles: [], completed_podcasts: [] };
    }
};

/**
 * Record a guest curiosity center completion
 */
export const recordGuestCuriosityInteraction = (contentType, contentId, interactionType, meta = {}) => {
    const interactions = getGuestCuriosityInteractions();
    let updated = false;
    const cid = parseInt(contentId);

    if (interactionType === 'completed' && !isNaN(cid)) {
        if (contentType === 'video') {
            if (!interactions.completed_videos.includes(cid)) {
                interactions.completed_videos.push(cid);
                updated = true;
                if (meta.points) addGuestXP(meta.points);
            }
        } else if (contentType === 'article') {
            if (!interactions.completed_articles.includes(cid)) {
                interactions.completed_articles.push(cid);
                updated = true;
                if (meta.points) addGuestXP(meta.points);
            }
        } else if (contentType === 'podcast') {
            if (!interactions.completed_podcasts.includes(cid)) {
                interactions.completed_podcasts.push(cid);
                updated = true;
                if (meta.points) addGuestXP(meta.points);
            }
        }
    }

    if (updated) {
        localStorage.setItem(CURIOSITY_INTERACTIONS_KEY, JSON.stringify(interactions));
    }
    return interactions;
};

